
import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { FileDown } from "lucide-react";
import { exportBookmarksToHtml } from "@/utils/htmlBookmarkExport";

interface ExportBookmarksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ExportBookmarksDialog: React.FC<ExportBookmarksDialogProps> = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [progressStage, setProgressStage] = useState("");

  const handleCancel = () => {
    if (!isExporting) {
      onOpenChange(false);
    }
  };

  const handleExport = async () => {
    if (!user) return;
    
    setIsExporting(true);
    setExportProgress(0);
    setProgressStage("Starting export...");
    
    try {
      console.log("Starting bookmark export process");
      
      // Call the export function with progress callback
      await exportBookmarksToHtml(
        user.id,
        (stage, progress) => {
          console.log(`Export progress: ${stage} - ${progress}%`);
          setProgressStage(stage);
          setExportProgress(progress);
        }
      );
      
      console.log("Export completed successfully");
      toast.success("Bookmarks exported successfully");
      
      // Close dialog
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error during export:", error);
      toast.error(`Export error: ${error.message || "Failed to export bookmarks"}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isExporting) onOpenChange(isOpen); // Only allow closing if not exporting
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Export Bookmarks as HTML</DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          {!isExporting ? (
            <div className="flex flex-col space-y-4 items-center justify-center py-6">
              <FileDown className="h-16 w-16 text-gray-400" />
              <div className="text-center">
                <p className="text-lg font-medium">Ready to export your bookmarks</p>
                <p className="text-sm text-muted-foreground mt-2">
                  This will create an HTML file that you can import into any browser.
                  All your collections will be exported as bookmark folders.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm font-medium mb-2">{progressStage}</p>
                <Progress value={exportProgress} className="h-2 w-full" />
                <p className="text-xs text-muted-foreground mt-1">
                  {exportProgress}% complete
                </p>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={isExporting}
          >
            {isExporting ? "Please wait..." : "Cancel"}
          </Button>
          <Button 
            type="submit" 
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? "Exporting..." : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExportBookmarksDialog;
